import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../services/apiClient';

const OrgContext = createContext(null);

/**
 * Stores the current organization (resolved from the /:orgCode URL prefix).
 * Provides the org code to apiClient via localStorage so that X-Tenant-Code
 * is sent on every request before the user is authenticated.
 */
export const OrgProvider = ({ orgCode, children }) => {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgCode) {
      setLoading(false);
      return;
    }

    const upper = orgCode.toUpperCase();
    let active = true;
    setOrg(null);
    setError(null);
    setLoading(true);
    if (localStorage.getItem('orgCode') !== upper) {
      localStorage.removeItem('orgCode');
    }

    apiClient.get(`/org/${upper}/info/`)
      .then(res => {
        if (!active) return;
        setOrg(res.data);
        setError(null);
        // Only persist once the org is confirmed to exist — apiClient reads
        // this to set X-Tenant-Code on every request, so storing an
        // unverified code here would let a mistyped/invalid org slug in the
        // URL send a bogus tenant header on the login request and beyond.
        localStorage.setItem('orgCode', upper);
      })
      .catch(() => {
        if (!active) return;
        setError(`Organization "${orgCode}" not found.`);
        setOrg(null);
        // Clear any stale orgCode so requests (including login) don't carry
        // a tenant header for an organization that doesn't actually exist.
        localStorage.removeItem('orgCode');
      })
      .finally(() => { if (active) setLoading(false); });

    // Note: do NOT remove orgCode on unmount — ProtectedRoute and apiClient
    // need it to build correct redirects even during route transitions.
    return () => { active = false; };
  }, [orgCode]);

  return (
    <OrgContext.Provider value={{ org, loading, error, orgCode: orgCode?.toUpperCase() }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => useContext(OrgContext);
